import { PropsWithChildren, useEffect, useState } from 'react';

import { BottomSheet } from '@ravago/shared/radiance/components/bottom-sheet/components/BottomSheet/BottomSheet';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { ClientPortal } from '@ravago/shared/radiance/components/client-portal/components/ClientPortal/ClientPortal';
import { Flyout, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Tabs } from '@ravago/shared/radiance/components/tabs/components/Tabs/Tabs';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { useBottomContainerContext } from '../../../../context/bottom-container-context';

interface Props extends PropsWithChildren {
   hasCustomerColors: boolean;
   productName?: string;
   activeTab: 'order-now' | 'rfq';
   onTabChange: (value: 'order-now' | 'rfq') => void;
}

export const DynamicProductBlockMobile = ({ hasCustomerColors, productName, activeTab, onTabChange, children }: Props) => {
   const translate = useTranslations();
   const [open, setOpen] = useState(false);
   const { setHasBottomSheet } = useBottomContainerContext();

   useEffect(() => {
      setHasBottomSheet(true);

      return () => {
         setHasBottomSheet(false);
      };
   }, [setHasBottomSheet]);

   const tabs = [{ label: translate('request-for-quotation.order-now') }, { label: translate('request-for-quotation.request-quote') }];
   const activeTabIndex = activeTab === 'order-now' ? 0 : 1;

   const handleTabChange = (index: number) => {
      onTabChange(index === 0 ? 'order-now' : 'rfq');
   };

   const handleOrderNowClick = () => {
      onTabChange('order-now');
      setOpen(true);
   };

   const handleGetQuoteClick = () => {
      onTabChange('rfq');
      setOpen(true);
   };

   return (
      <>
         <ClientPortal selector="body">
            <BottomSheet
               container={false}
               header={
                  <Box direction="column" gap="md">
                     {productName && (
                        <Typography variant="small" color="subtle">
                           {productName}
                        </Typography>
                     )}
                     <Box gap="sm">
                        {hasCustomerColors && (
                           <Button fullWidth onClick={handleOrderNowClick}>
                              {translate('request-for-quotation.order-now')}
                           </Button>
                        )}
                        <Button fullWidth variant="secondary" onClick={handleGetQuoteClick}>
                           {translate('request-for-quotation.request-quote')}
                        </Button>
                     </Box>
                  </Box>
               }
               viewport
            />
         </ClientPortal>

         <Flyout open={open} onFlyoutClose={() => setOpen(false)}>
            <FlyoutHeader>
               {hasCustomerColors ? (
                  <Tabs tabs={tabs} activeTab={activeTabIndex} onTabChange={handleTabChange} />
               ) : (
                  translate('request-for-quotation.request-quote')
               )}
            </FlyoutHeader>
            {children}
         </Flyout>
      </>
   );
};
